import React from 'react';
import { Button, ButtonVariant, ButtonSize } from '../ui/Button';
import { cn } from '../../utils/cn';

export type InnerLayoutVariant = 'default' | 'fullHeight' | 'tab';

export interface HeaderActionItem {
  id?: string;
  label: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }> | React.ReactNode;
  onClick?: () => void;
  href?: string;
  to?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

export interface PillNavItem {
  id: string;
  label: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }> | React.ReactNode;
  badge?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  danger?: boolean;
  onClick?: () => void;
  href?: string;
  className?: string;
}

export interface HeaderActionsProps {
  actions?: HeaderActionItem[] | React.ReactNode;
  defaultSize?: ButtonSize;
  className?: string;
}

/**
 * Recycled, standardized action button list for page and section headers.
 * Guaranteed 1:1 identical button sizing, font size, paddings, and alignment everywhere.
 */
export const HeaderActions: React.FC<HeaderActionsProps> = ({
  actions,
  defaultSize = 'sm',
  className = '',
}) => {
  if (!actions) return null;

  if (Array.isArray(actions)) {
    if (actions.length === 0) return null;

    return (
      <div className={cn('flex items-center gap-2 flex-wrap sm:flex-nowrap shrink-0', className)}>
        {actions.map((act, idx) => {
          const actKey = act.id || idx;
          const variant: ButtonVariant = act.variant || (idx === 0 ? 'primary' : 'secondary');
          const size: ButtonSize = act.size || defaultSize;

          const renderActionIcon = () => {
            if (!act.icon) return null;
            if (React.isValidElement(act.icon)) {
              return act.icon;
            }
            const IconComp = act.icon as React.ComponentType<{ size?: number; className?: string }>;
            return <IconComp size={14} />;
          };

          const targetTo = act.to || act.href;

          if (targetTo) {
            return (
              <Button
                key={actKey}
                to={targetTo}
                variant={variant}
                size={size}
                disabled={act.disabled}
                isLoading={act.loading}
                onClick={act.onClick}
                leftIcon={renderActionIcon()}
                className={cn('whitespace-nowrap shrink-0', act.className)}
              >
                {act.label}
              </Button>
            );
          }

          return (
            <Button
              key={actKey}
              type="button"
              variant={variant}
              size={size}
              disabled={act.disabled}
              isLoading={act.loading}
              onClick={act.onClick}
              leftIcon={renderActionIcon()}
              className={cn('whitespace-nowrap shrink-0', act.className)}
            >
              {act.label}
            </Button>
          );
        })}
      </div>
    );
  }

  return <div className={cn('flex items-center gap-2 shrink-0', className)}>{actions}</div>;
};

export interface InnerLayoutHeaderProps {
  title: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }> | React.ReactNode;
  description?: React.ReactNode;
  desc?: React.ReactNode;
  actions?: HeaderActionItem[] | React.ReactNode;
  className?: string;
}

/**
 * Standardized, recycled page header.
 * Enforces identical typography, padding, and action button layout across all views.
 */
export const InnerLayoutHeader: React.FC<InnerLayoutHeaderProps> = ({
  title,
  icon,
  description,
  desc,
  actions,
  className = '',
}) => {
  const resolvedDescription = description ?? desc;

  const renderIcon = () => {
    if (!icon) return null;
    if (React.isValidElement(icon)) {
      return icon;
    }
    const IconComp = icon as React.ComponentType<{ size?: number; className?: string }>;
    return <IconComp size={20} className="text-cf-orange flex-shrink-0" />;
  };

  return (
    <header className={cn('flex flex-col flex-shrink-0 mb-3 sm:mb-5', className)}>
      <div className={cn('flex justify-between flex-wrap gap-4', resolvedDescription ? 'items-start' : 'items-center')}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5 flex-wrap">
            {renderIcon()}
            <h1 className="text-lg font-bold text-cf-textPrimary flex items-center gap-2 tracking-tight">
              {title}
            </h1>
          </div>
          {resolvedDescription && (
            <p className="text-xs text-cf-textSecondary mt-1 max-w-2xl leading-relaxed">
              {resolvedDescription}
            </p>
          )}
        </div>

        {actions && <HeaderActions actions={actions} defaultSize="sm" />}
      </div>
    </header>
  );
};

export interface InnerLayoutSectionProps {
  title: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }> | React.ReactNode;
  description?: React.ReactNode;
  desc?: React.ReactNode;
  actions?: HeaderActionItem[] | React.ReactNode;
  className?: string;
  headerClassName?: string;
  children: React.ReactNode;
}

/**
 * Standardized, recycled section component for inner layout sections.
 * Enforces consistent section headings, descriptions, and action buttons.
 */
export const InnerLayoutSection: React.FC<InnerLayoutSectionProps> = ({
  title,
  icon,
  description,
  desc,
  actions,
  className = '',
  headerClassName = '',
  children,
}) => {
  const resolvedDescription = description ?? desc;

  const renderIcon = () => {
    if (!icon) return null;
    if (React.isValidElement(icon)) {
      return icon;
    }
    const IconComp = icon as React.ComponentType<{ size?: number; className?: string }>;
    return <IconComp size={16} className="text-cf-orange flex-shrink-0" />;
  };

  return (
    <section className={cn('space-y-3.5 sm:space-y-4', className)}>
      <div className={cn('flex items-start sm:items-center justify-between flex-wrap gap-3', headerClassName)}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {renderIcon()}
            <h2 className="text-sm font-semibold text-cf-textPrimary flex items-center gap-2 tracking-tight">
              {title}
            </h2>
          </div>
          {resolvedDescription && (
            <p className="text-xs text-cf-textSecondary mt-0.5 max-w-2xl leading-relaxed">
              {resolvedDescription}
            </p>
          )}
        </div>

        {actions && <HeaderActions actions={actions} defaultSize="sm" />}
      </div>

      {children}
    </section>
  );
};

export interface PillNavBarProps {
  items?: PillNavItem[];
  className?: string;
}

/**
 * Standardized PillNavBar enforcing horizontal scrolling, zero text wrapping (shrink-0 whitespace-nowrap),
 * smooth mobile touch momentum scrolling, and identical design tokens across all views.
 */
export const PillNavBar: React.FC<PillNavBarProps> = ({ items, className = '' }) => {
  if (!items || items.length === 0) return null;

  const isAllHidden = items.every((item) => item.className?.split(/\s+/).includes('hidden'));
  if (isAllHidden) return null;

  const isAllMdHidden = items.every((item) => item.className?.split(/\s+/).includes('md:hidden'));

  return (
    <nav
      aria-label="Sub-navigation"
      className={cn(
        'flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 px-0.5 shrink-0 max-w-full',
        isAllMdHidden && 'md:hidden',
        className
      )}
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      {items.map((item) => {
        const isActive = Boolean(item.active);
        const isDanger = Boolean(item.danger);

        const baseClasses =
          'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium shrink-0 whitespace-nowrap transition-colors cursor-pointer outline-none focus:outline-none select-none';

        let colorClasses = '';
        if (isDanger) {
          colorClasses = isActive
            ? 'bg-rose-500/15 text-rose-600 dark:text-rose-400 border border-rose-500/30 font-semibold'
            : 'text-cf-textSecondary hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-500/10 border border-transparent';
        } else if (isActive) {
          colorClasses =
            'bg-cf-surfaceHover text-cf-textPrimary border border-cf-border/80 shadow-xs font-semibold';
        } else {
          colorClasses =
            'text-cf-textSecondary hover:text-cf-textPrimary hover:bg-cf-surfaceHover/60 border border-transparent';
        }

        const renderIcon = () => {
          if (!item.icon) return null;
          if (React.isValidElement(item.icon)) {
            return item.icon;
          }
          const IconComp = item.icon as React.ComponentType<{ size?: number; className?: string }>;
          return (
            <IconComp
              size={14}
              className={
                isActive
                  ? isDanger
                    ? 'text-rose-500'
                    : 'text-cf-textPrimary'
                  : isDanger
                  ? 'text-rose-400'
                  : 'text-cf-textMuted'
              }
            />
          );
        };

        if (item.href) {
          return (
            <Button
              key={item.id}
              to={item.href}
              onClick={item.onClick}
              leftIcon={renderIcon()}
              rightIcon={item.badge}
              className={cn(baseClasses, colorClasses, item.className)}
            >
              {item.label}
            </Button>
          );
        }

        return (
          <Button
            key={item.id}
            type="button"
            disabled={item.disabled}
            onClick={item.onClick}
            leftIcon={renderIcon()}
            rightIcon={item.badge}
            className={cn(
              baseClasses,
              colorClasses,
              item.disabled && 'opacity-50 cursor-not-allowed',
              item.className
            )}
          >
            {item.label}
          </Button>
        );
      })}
    </nav>
  );
};

export interface InnerLayoutProps {
  header?: InnerLayoutHeaderProps;
  navItems?: PillNavItem[];

  // Backwards-compatible direct props
  title?: React.ReactNode;
  icon?: React.ComponentType<{ size?: number; className?: string }> | React.ReactNode;
  description?: React.ReactNode;
  desc?: React.ReactNode;
  actions?: HeaderActionItem[] | React.ReactNode;
  pills?: React.ReactNode;

  variant?: InnerLayoutVariant;
  className?: string;
  children: React.ReactNode;
}

/**
 * Standardized InnerLayout enforcing unified header hierarchy and vertical rhythm across the entire app.
 */
export const InnerLayout: React.FC<InnerLayoutProps> = ({
  header,
  navItems,
  title,
  icon,
  description,
  desc,
  actions,
  pills,
  variant = 'default',
  className = '',
  children,
}) => {
  // Resolve header fields with priority to structured `header` prop, falling back to direct props
  const resolvedTitle = header?.title ?? title;
  const resolvedIcon = header?.icon ?? icon;
  const resolvedDescription = header?.description ?? header?.desc ?? description ?? desc;
  const resolvedActions = header?.actions ?? actions;

  let contentVariantClasses = 'space-y-4 pb-8 sm:pb-6';

  if (variant === 'fullHeight') {
    contentVariantClasses = 'flex-1 flex flex-col min-w-0 max-w-full overflow-hidden min-h-0';
  } else if (variant === 'tab') {
    contentVariantClasses = 'space-y-8 pb-6 min-w-0 max-w-full';
  }

  return (
    <div className={cn('w-full min-w-0 max-w-full', contentVariantClasses, className)}>
      {/* Centralized Standard Header */}
      <InnerLayoutHeader
        title={resolvedTitle}
        icon={resolvedIcon}
        description={resolvedDescription}
        actions={resolvedActions}
      />

      {/* Centralized Standard Navigation Pills (supports typed navItems or legacy pills) */}
      {(() => {
        if (navItems && navItems.length > 0) {
          const isAllHidden = navItems.every((item) => item.className?.split(/\s+/).includes('hidden'));
          if (isAllHidden) return null;

          const isAllMdHidden = navItems.every((item) => item.className?.split(/\s+/).includes('md:hidden'));
          return (
            <div className={cn('mt-3.5 sm:mt-4 mb-4 sm:mb-6', isAllMdHidden && 'md:hidden')}>
              <PillNavBar items={navItems} />
            </div>
          );
        }
        if (pills) {
          return (
            <div
              className="mt-3.5 sm:mt-4 mb-4 sm:mb-6 flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1 px-0.5 shrink-0 max-w-full [&>*]:shrink-0 [&>*]:whitespace-nowrap"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {pills}
            </div>
          );
        }
        return null;
      })()}

      {/* Main Content Area */}
      {children}
    </div>
  );
};
